export const CASE_STUDIES = {
  "ai-post-call": {
    slug: "ai-post-call",
    title: "AI-Powered Post-Call Product-Led Strategy",
    category: "Software Development",
    tagline:
      "Designed a product-led strategy to streamline post-call workflows for technical sales teams.",
    image:
      "https://framerusercontent.com/images/HH6KjvaovebOIdq46oRjg7XPs0.webp?width=1200&height=630",
    meta: [
      { k: "Industry", v: "Software Development" },
      { k: "Headquarters", v: "Seattle, Washington" },
      { k: "Founded", v: "2012" },
      { k: "Company size", v: "501–1000" },
    ],
    stats: [
      { value: "95%", label: "Reps see value in AI-assisted follow-up" },
      { value: "66%", label: "Projected reduction in decision time" },
      { value: "25%", label: "Projected conversion-rate lift" },
    ],
    sections: [
      {
        heading: "Challenge",
        type: "text",
        body: "Develop a product-led strategy to address inefficiencies in technical sales representatives' post-call workflows. The project required identifying pain points in follow-up decision-making, evaluating AI adoption readiness among sales reps, and designing a data-backed solution that improves productivity without compromising the human element of sales.",
      },
      {
        heading: "My Role & Approach",
        type: "list",
        items: [
          "Conducted primary research surveying 20 technical sales representatives to understand post-call behaviour and challenges.",
          "Analyzed quantitative and qualitative data to identify key pain points in decision-making workflows.",
          "Assessed market readiness for AI-driven sales tools and identified adoption barriers.",
          "Designed a product strategy with AI-powered recommendations and human-in-the-loop workflows.",
          "Defined measurable success metrics (OKRs) to track adoption, efficiency and business impact.",
        ],
      },
      {
        heading: "Critical Pain Points Identified",
        type: "list",
        items: [
          "Reps spend 10–30 minutes per call deciding next steps — a significant productivity drain.",
          "85% of reps experience uncertainty about the right follow-up actions.",
          "Average confidence in follow-up effectiveness rated only 6–7 out of 10.",
          "Time-consuming decisions lead to delayed follow-ups and inconsistent deal progression.",
        ],
      },
      {
        heading: "AI Adoption Insights",
        type: "list",
        items: [
          "95% of surveyed reps see strong value in AI-assisted follow-up tools.",
          "Key adoption factors: accuracy, personalization and maintaining human judgment.",
          "Primary concern is 'sounding robotic' rather than data security.",
          "Reps want AI as a 'copilot, not autopilot' — suggesting actions while keeping control.",
        ],
      },
      {
        heading: "Strategic Recommendations",
        type: "list",
        items: [
          "AI-Powered Next-Step Recommender: analyze call transcripts + CRM data for 3–4 tailored action suggestions with smart prioritization.",
          "One-click follow-up templates with personalized, editable drafts.",
          "Human-in-the-loop design: AI transparency mode explaining reasoning, with rep approve / edit / discard control.",
          "Contextual personalization engine using account history and deal stage.",
        ],
      },
      {
        heading: "Projected Business Impact",
        type: "list",
        items: [
          "Reduce decision-making time from 10–30 minutes to under 10 minutes per call.",
          "Decrease time-to-follow-up from 24 hours to 12 hours.",
          "Reduce rep uncertainty from 65% to under 25%.",
          "Improve deal velocity by 10% and conversion rates by 25% within 6 months.",
          "Target a 70% adoption rate within 3 months of launch.",
        ],
      },
    ],
    docLabel: "Read Highspot's Product-Led Document",
    docLink:
      "https://www.notion.so/Highspot-Product-Led-Strategy-Post-Call-Guidance-2685405b878580e4ac98c630c7a61840",
  },

  community: {
    slug: "community",
    title: "CommUnity — Social Platform for International Student Integration",
    category: "Social Networking",
    tagline:
      "A mobile-first social platform to address the severe cultural isolation faced by international students relocating to new countries.",
    image:
      "https://framerusercontent.com/images/hJcR2l79WIANAbewoSaFfDQNYhc.png?width=1536&height=1024",
    meta: [
      { k: "Industry", v: "Social Networking" },
      { k: "Founded", v: "2025" },
      { k: "Stage", v: "MVP Development" },
      { k: "Role", v: "Product Manager" },
    ],
    stats: [
      { value: "500+", label: "Target active users in 6 months" },
      { value: "90%+", label: "Connection acceptance goal" },
      { value: "40%+", label: "Target user retention" },
    ],
    sections: [
      {
        heading: "Overview",
        type: "text",
        body: "International students face a critical challenge existing social platforms fail to address: severe cultural isolation when relocating to a new country. Platforms like Facebook and WhatsApp exist for broad networking, but they don't help students find people from their specific hometown or region who can provide authentic cultural understanding and practical guidance. This gap increases stress and reduces academic performance during the crucial first year abroad.",
      },
      {
        heading: "Research Insights",
        type: "list",
        items: [
          "Students struggle to find their hometown community, relying on fragmented Facebook groups.",
          "They lack critical local knowledge — transit, banking, groceries, part-time jobs.",
          "They hesitate to connect with strangers online without verification systems.",
        ],
      },
      {
        heading: "Solution & Process",
        type: "text",
        body: "I led end-to-end product development for CommUnity, a mobile-first platform connecting international students with verified community members from their hometown or region.",
      },
      {
        heading: "Product Definition",
        type: "list",
        items: [
          "Conducted primary research through structured surveys and customer interviews to validate the problem.",
          "Developed a product strategy: vision statement, value proposition and competitive positioning via verified cultural matching and geo-location connections.",
          "Wrote a detailed PRD with user personas, 30+ user stories across 4 epics, and success metrics.",
          "Designed low-fidelity wireframes and ran usability testing with 5 participants across 5 task flows.",
        ],
      },
      {
        heading: "Go-to-Market & Roadmap",
        type: "list",
        items: [
          "Positioning framework and freemium pricing model with targeted advertising.",
          "Defined KPIs: 500 user acquisition / month, 30%+ MAU, 4.5+ CSAT, 10% free-to-premium conversion.",
          "Set OKRs targeting 500+ active users across 5 pilot communities within 6 months.",
          "Created a phased 6-week roadmap: auth, profiles, discovery, messaging, events and safety.",
        ],
      },
    ],
    docLabel: "Access the CommUnity PRD",
    docLink:
      "https://docs.google.com/document/d/1F8iulQaba0G5grlKdX7gyIWCwQK8QcUXb-iwvPiw-HY/edit?usp=sharing",
  },

  "market-expansion": {
    slug: "market-expansion",
    title: "Market Expansion Strategy for Renewable Energy Provider",
    category: "Energy & Utilities",
    tagline:
      "Strategic analysis identifying high-potential South American markets for renewable energy expansion.",
    image:
      "https://framerusercontent.com/images/9qJAo2mmecyr9WeSPdmq9d8TU0.png?width=1200&height=600",
    meta: [
      { k: "Industry", v: "Energy & Utilities" },
      { k: "Headquarters", v: "Edmonton, Canada" },
      { k: "Founded", v: "2009" },
      { k: "Company size", v: "501–1000" },
    ],
    stats: [
      { value: "3", label: "High-potential markets identified" },
      { value: "5-Yr", label: "Financial assessment (2017–2021)" },
      { value: "4P", label: "Integrated marketing mix defined" },
    ],
    sections: [
      {
        heading: "Challenge",
        type: "text",
        body: "Analyze Capital Power Corporation's strategic positioning and identify viable international expansion opportunities for its renewable energy operations. This required a comprehensive assessment of financial performance, market dynamics and competitive landscape to develop a data-backed market-entry strategy for sustainable growth.",
      },
      {
        heading: "My Role & Approach",
        type: "list",
        items: [
          "Conducted comprehensive SWOT and PESTEL analyses to assess strategic positioning.",
          "Performed financial analysis of liquidity, profitability, solvency and activity ratios (2017–2021).",
          "Identified South American markets — Brazil, Argentina, Chile — as high-potential opportunities.",
          "Developed an integrated marketing communications strategy and GTM roadmap.",
          "Created financial forecasts for revenue and COGS to project expansion viability.",
        ],
      },
      {
        heading: "Market Opportunity",
        type: "list",
        items: [
          "South American countries struggle to meet growing electricity demand while relying on fossil fuels.",
          "Significant opportunity to provide low-carbon renewable energy solutions.",
          "Existing political and regulatory understanding from US operations is transferable.",
        ],
      },
      {
        heading: "Strategic Recommendations",
        type: "list",
        items: [
          "Direct marketing via electronic newsletters, websites and catalogs to reach B2B customers.",
          "Public relations initiatives modelled on the $2M community investment program to build awareness.",
          "Leverage the 4P mix: existing renewable tech, market-driven pricing, strategic facility placement, development-focused promotion.",
        ],
      },
      {
        heading: "Financial Insights",
        type: "list",
        items: [
          "Revenue forecasted to trend upward, supporting expansion timing.",
          "Identified the need for better cost management as COGS is projected to rise.",
          "Inventory turnover improved over the period, indicating operational efficiency gains.",
          "Flagged potential liquidity concerns requiring proactive financial planning.",
        ],
      },
    ],
    docLabel: "Read the Strategic Growth Document",
    docLink:
      "https://www.notion.so/Strategic-Market-Expansion-2af5405b8785802b8f72f538c0e33403",
  },

  "growth-strategy": {
    slug: "growth-strategy",
    title: "Growth Strategy for Global Payment Technology Platform",
    category: "Fintech",
    tagline:
      "A comprehensive business strategy for a payment-processing leader — expansion opportunities, distribution channels and international markets.",
    image:
      "https://framerusercontent.com/images/wQRXqH72IP7yyWAfcD4vqHbH0M.jpg?width=1323&height=693",
    meta: [
      { k: "Industry", v: "Fintech" },
      { k: "Headquarters", v: "Montreal, Canada" },
      { k: "Founded", v: "2003" },
      { k: "Company size", v: "1,001–5,000" },
    ],
    stats: [
      { value: "$5.75B", label: "Market capitalization" },
      { value: "200+", label: "Markets served globally" },
      { value: "600+", label: "Payment methods supported" },
    ],
    sections: [
      {
        heading: "Challenge",
        type: "text",
        body: "Analyze Nuvei's competitive positioning in the highly competitive payment-processing industry and develop a comprehensive growth strategy to expand market share, retain customers and identify new revenue opportunities — evaluating the business model, assessing competitive threats across three tiers, and creating actionable recommendations for sustainable growth.",
      },
      {
        heading: "My Role & Approach",
        type: "list",
        items: [
          "Conducted competitive landscape analysis across direct competitors (PayPal, Stripe), indirect competitors and new entrants.",
          "Performed PESTLE analysis to assess external environmental factors.",
          "Evaluated strategic partnerships (Microsoft, Shopify, Mercedes-AMG Petronas F1).",
          "Developed a multi-pronged growth strategy across acquisition, retention, expansion and diversification.",
        ],
      },
      {
        heading: "Market Position",
        type: "list",
        items: [
          "Operates in 200+ markets with 600+ payment methods and 150+ currencies.",
          "Differentiation through customized payment solutions vs. one-size-fits-all competitors.",
          "Technical advantages: Dynamic Currency Conversion, Smart Transaction Routing, robust PCI compliance.",
          "2020 IPO raised $700M USD — Canada's most valuable tech IPO to date.",
        ],
      },
      {
        heading: "Growth Strategy Recommendations",
        type: "list",
        items: [
          "Acquisition: free trials and demos, referral programs, and emerging tech (Blockchain, AI) for advantage.",
          "Retention: personalized service, value-added fraud tools, and customized pricing packages.",
          "Market expansion: new verticals (non-profits, gaming), online marketplace and bank partnerships.",
          "Beyond core: acquisitions of complementary companies and adjacent financial-management products.",
        ],
      },
    ],
    docLabel: "Read the Nuvei Growth Strategy Doc",
    docLink:
      "https://www.notion.so/Growth-Strategy-for-the-Future-of-Payments-A-Deep-Dive-into-Nuvei-s-Expansion-Opportunities-2af5405b878580c9b72bdc6314fcd15c",
  },
};

export const getCaseStudy = (slug) => CASE_STUDIES[slug];
export const CASE_ORDER = ["ai-post-call", "community", "market-expansion", "growth-strategy"];
